import { useEffect, useState } from 'react';

import { Drawer, InputAdornment } from '@material-ui/core';
import { OPEN_ENGAGEMENT_QUESTION_PANEL } from 'Deer/pages/Company/Engagement/Questions/constants/AddQuestionConstant';
import APIError from 'components/AlertScreens/APIError';
import EmptyError from 'components/AlertScreens/EmptyError';
import ListSkeletonLoader from 'components/AlertScreens/ListSkeletonLoader';
import useAsync from 'components/hooks/useAsync';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Select from 'react-select';
import { useEngagementTemplate } from 'screens/admin/Engagement/EngagementTemplate/Context/EngagementContext';
import NewSidepanelHeader from 'screens/admin/common/NewSidepanelHeader';
import { selectQuestionDetails } from 'selectors/questionDetails';
import styled from 'styled-components';
import { Divider1, StyledBox, StyledButton, StyledGrid, StyledIconButton } from 'styles/common';

import {
  QUESTION_FORM_SOURCES,
  REVIEW_QUESTION_TYPE_MAP_1,
  REVIEW_QUESTION_TYPE_MAP_2,
} from 'constants/ProjectConstants';
import { CrossIcon, SearchIcon } from 'constants/icons';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { showQuestionDetails } from 'services/questionDetails';
import { useGetQuestionsQuery, getQuestionsWithCustomDescription } from 'services/reviews';

import { Typography10, Typography13 } from 'uiToolkit/Typography';

import { useReviewTemplate } from '../../../Reviews/ReviewTemplate/common/ReviewTemplateContext';
import { TemplateTextField } from '../../../Reviews/ReviewTemplate/common/styles';
import TemplateQuestionList from './QuestionList';
import { useQuestionListSidePanel } from './context/QuestionListSidepanelContext';

const ALL_QUESTIONS = 'ALL_QUESTIONS';

const ENGAGEMENT_QUESTION_TYPE_OPTIONS = [
  {
    label: 'All',
    value: ALL_QUESTIONS,
  },
  {
    label: 'Emoji',
    value: 'EMOJI',
  },
  {
    label: 'Number',
    value: 'NUMBER_RATING',
  },
  {
    label: 'Options',
    value: 'DROP_DOWN',
  },
  {
    label: 'Text',
    value: 'NONE',
  },
];

const QuestionListSidepanel = ({ source = QUESTION_FORM_SOURCES.REVIEW }) => {
  const [filters, setFilters] = useState({
    text: '',
    answerType: ENGAGEMENT_QUESTION_TYPE_OPTIONS[0].value,
  });
  const [filteredData, setFilteredData] = useState(null);
  const location = useLocation();
  const history = useHistory();

  const {
    state: { open, onSuccess, onClose, onCreateNew, type, competencyId, insertionPosition },
    hideSidepanel,
  } = useQuestionListSidePanel();
  const { state: addedReviewTemplatedQuestions } = useReviewTemplate();
  const { state: engagementTemplate } = useEngagementTemplate();

  const {
    data: reviewQuestions,
    run,
    isError: isReviewQuestionsError,
    isLoading: isReviewQuestionsLoading,
    setData,
  } = useAsync();

  const {
    data: engagementQuestions,
    isFetching: isEngagementQuestionsLoading,
    isError: isEngagementQuestionsError,
  } = useGetQuestionsQuery({
    source,
    reactQueryKey: [REACT_QUERY_KEYS.engagement.getQuestions],
    enabled: source === QUESTION_FORM_SOURCES.ENGAGEMENT,
  });

  const data = source === QUESTION_FORM_SOURCES.REVIEW ? reviewQuestions : engagementQuestions;
  const isLoading = source === QUESTION_FORM_SOURCES.REVIEW ? isReviewQuestionsLoading : isEngagementQuestionsLoading;
  const isError = source === QUESTION_FORM_SOURCES.REVIEW ? isReviewQuestionsError : isEngagementQuestionsError;

  const dispatch = useDispatch();

  // this is used for review module only
  const { updatedQuestion: newQuestion } = useSelector(selectQuestionDetails, shallowEqual);

  const handleClose = () => {
    onClose && onClose();
    hideSidepanel();
  };

  const getData = () => {
    run(getQuestionsWithCustomDescription({ limit: 4000, source }).then((res) => res));
  };

  useEffect(() => {
    if (open && source === QUESTION_FORM_SOURCES.REVIEW) {
      getData();
    }
  }, [open]);

  const handleAddQuestion = (question) => {
    onSuccess({ question, insertionPosition });
  };

  const getExclusionsList = () => {
    let templateQuestions = [];
    if (source === QUESTION_FORM_SOURCES.REVIEW) {
      if (type === REVIEW_QUESTION_TYPE_MAP_2.POTENTIAL) {
        if (competencyId) {
          templateQuestions =
            addedReviewTemplatedQuestions[REVIEW_QUESTION_TYPE_MAP_1[type]].templateQuestions[competencyId];
        }
      } else {
        templateQuestions = addedReviewTemplatedQuestions[REVIEW_QUESTION_TYPE_MAP_1[type]]?.templateQuestions;
      }
    }
    if (source === QUESTION_FORM_SOURCES.ENGAGEMENT) {
      templateQuestions = engagementTemplate?.templateQuestions;
    }
    return templateQuestions?.map((ques) => ques.question.id) || [];
  };

  // Filters questions according to the place from where it opened: ["GOAL", "COMPETENCY", "QNA"]
  // If competencyId(only for Competency Review), questions with that competencyId or without any competency associated
  // If Search text typed, filter over the type filtered data.
  const handleFilteration = () => {
    let newData = [...data];
    if (source === QUESTION_FORM_SOURCES.REVIEW) {
      newData = [...newData.filter((question) => question.questionType === type)];
      if (competencyId) {
        newData = [...newData.filter((question) => question.tagId === competencyId || question.tag === null)];
      } else {
        newData = [...newData.filter((question) => !question.tag)];
      }
    }
    if (filters.text) {
      newData = newData.filter((item) => item.description?.toLowerCase().includes(filters.text.toLowerCase()));
    }
    if (filters.answerType && filters.answerType !== ALL_QUESTIONS) {
      newData = newData.filter((item) => item.answerType === filters.answerType);
    }
    setFilteredData(newData);
  };

  useEffect(() => {
    if (data) {
      handleFilteration();
    }
  }, [data, filters]);

  const setFilter = (param, value) => {
    setFilters({
      ...filters,
      [param]: value,
    });
  };

  const handleCreateQuestion = () => {
    if (source === QUESTION_FORM_SOURCES.ENGAGEMENT) {
      const queryParams = new URLSearchParams(location.search);
      queryParams.set('action', 'add');
      queryParams.set(OPEN_ENGAGEMENT_QUESTION_PANEL, String(true));
      history.push({
        search: queryParams.toString(),
      });
    } else {
      dispatch(showQuestionDetails({ questionId: 0, action: 'create', source, history }));
    }
  };

  // this is used for review module only
  useEffect(() => {
    if (newQuestion && data) {
      const newData = [newQuestion, ...data];
      setData(newData);
    }
  }, [newQuestion]);

  const LeftActionComp = (
    <StyledBox flexcontainer justifycontent="center" alignitems="center">
      <StyledIconButton onClick={handleClose}>
        <CrossIcon size="fixed12px" />
      </StyledIconButton>
      <Typography13 display="inline" customColor="textPrimary" variant="h6" leftGutter={1}>
        Question Library
      </Typography13>
    </StyledBox>
  );

  const RightActionComp = (
    <StyledButton color="primary" onClick={handleCreateQuestion}>
      Create New
    </StyledButton>
  );

  return (
    <StyledDrawer
      open={open}
      onClose={handleClose}
      anchor="right"
      className="mesh-app-drawer"
      // Focus fighting between chakra drawer and mui drawer
      // REFER: https://v4.mui.com/api/modal/#props
      ModalProps={{ onBackdropClick: handleClose, disableEnforceFocus: true }}
    >
      <NewSidepanelHeader leftAction={LeftActionComp} rightAction={RightActionComp} />
      <ParentContainer>
        <Section>
          <TemplateTextField
            onChange={(e) => setFilter('text', e.target.value)}
            variant="outlined"
            placeholder="Search questions"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size="tinier" />
                </InputAdornment>
              ),
            }}
          />
        </Section>
        <Divider1 topGutter={2} bottomGutter={2} />
        {source === QUESTION_FORM_SOURCES.ENGAGEMENT ? (
          <>
            <StyledGrid padding="0rem 2rem" container justify="flex-start">
              <StyledGrid item xs={12}>
                <Typography10 bottomGutter={2} variant="body2">
                  Question Type
                </Typography10>
              </StyledGrid>
              <StyledGrid item xs={6}>
                <Select
                  options={ENGAGEMENT_QUESTION_TYPE_OPTIONS}
                  onChange={(option) => setFilter('answerType', option.value)}
                  value={ENGAGEMENT_QUESTION_TYPE_OPTIONS.find((option) => option.value === filters.answerType)}
                />
              </StyledGrid>
            </StyledGrid>
            <Divider1 topGutter={2} bottomGutter={3} />
          </>
        ) : null}
        {isError ? (
          <APIError errorMessage="Something went wrong" errorSubtitle="Please try again" />
        ) : isLoading ? (
          <ListSkeletonLoader />
        ) : filteredData?.length ? (
          <TemplateQuestionList
            data={filteredData}
            handleAddQuestion={handleAddQuestion}
            type={type}
            exclusions={getExclusionsList()}
            source={source}
          />
        ) : (
          <EmptyError />
        )}
      </ParentContainer>
    </StyledDrawer>
  );
};

export default QuestionListSidepanel;

const StyledDrawer = styled(Drawer)`
  ${(props) => props.theme.breakpoints.up('sm')} {
    .MuiDrawer-paper {
      width: 40%;
      overflow: hidden;
    }
  }
`;

const ParentContainer = styled.div`
  overflow: hidden;
`;

const Section = styled.div`
  padding: 1rem 2rem 0rem 2rem;
`;
